import { mainClient } from 'apollo/client';
import Error404 from 'components/desktop/error-404';
import MobileError404 from 'components/mobile/error-404';
import {
  DEFAULT_DESCRIPTION,
  DEFAULT_OG_IMAGE,
  DEFAULT_TITLE,
  MAIN_DOMAIN,
  SITE_MAP,
} from 'globalConstants';
import {
  DistributedProject,
  DistributedProjectPagination,
  FileWithFullUrls,
  GET_DETAIL_PROJECTS_ATMNHA,
  GET_RELATED_PUBLISHED_PROJECTS_ATMNHA,
  GetDetailProjectsATMNhaData,
  GetDetailProjectsATMNhaVariables,
  GetRelatedPublishedProjectsATMNhaData,
  GetRelatedPublishedProjectsATMNhaVariables,
} from 'graphql/main/queries';
import { Omit, isArray, isEqual } from 'lodash';
import type { GetServerSideProps, NextPage } from 'next';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { Context, Fragment, createContext } from 'react';
import { detectDeviceByUserAgent } from 'utils';

const DetailProject = dynamic(() => import('components/desktop/detail-project'));
const MobileDetailProject = dynamic(() => import('components/mobile/detail-project'));

const DEFAULT_LIMIT_CARD_ITEM = 3;

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { req } = context;
  const { headers } = req;
  const device = detectDeviceByUserAgent(headers['user-agent']);
  let pageData = null;
  const { [SITE_MAP.PROJECT.DETAIL_PROJECT.INDEX]: detailProjectPathname } = context.query ?? {};
  try {
    const { data: getDetailProjectsATMNhaData } = await mainClient.query<
      GetDetailProjectsATMNhaData,
      GetDetailProjectsATMNhaVariables
    >({
      query: GET_DETAIL_PROJECTS_ATMNHA,
      variables: {
        id: detailProjectPathname
          ? isArray(detailProjectPathname)
            ? ''
            : detailProjectPathname
          : '',
      },
    });
    const { getDetailProjectsATMNha: project } = getDetailProjectsATMNhaData;

    if (project) {
      const { data: getRelatedPublishedProjectsATMNhaData } = await mainClient.query<
        GetRelatedPublishedProjectsATMNhaData,
        GetRelatedPublishedProjectsATMNhaVariables
      >({
        query: GET_RELATED_PUBLISHED_PROJECTS_ATMNHA,
        variables: {
          id: detailProjectPathname
            ? isArray(detailProjectPathname)
              ? ''
              : detailProjectPathname
            : '',
          limit: DEFAULT_LIMIT_CARD_ITEM,
          sort: '-createAt',
        },
      });
      const { getRelatedPublishedProjectsATMNha: relatedProjectsWithPagination } =
        getRelatedPublishedProjectsATMNhaData;

      pageData = {
        project: project ?? null,
        relatedProjectsWithPagination: relatedProjectsWithPagination ?? null,
      };
    }
  } catch (error) {
    console.log(error);
  }

  return {
    props: {
      device,
      pageData,
    },
  };
};

export const DetailProjectPageContext: Context<Omit<Props, 'device'>> = createContext({});

interface Props {
  device?: 'mobile' | 'desktop';
  pageData?: {
    project?: DistributedProject | null;
    relatedProjectsWithPagination?: DistributedProjectPagination | null;
  } | null;
}

const DetailProjectPage: NextPage = (props: Props) => {
  const { device, ...restProps } = props;
  const { pageData } = restProps;
  const { project } = pageData?.project ?? {};
  const { projectName, projectOverview, mediaUrls } = project ?? {};
  const router = useRouter();
  const { [SITE_MAP.PROJECT.DETAIL_PROJECT.INDEX]: detailProjectPathname } = router.query;
  const title = projectName ? `${projectName} | ${DEFAULT_TITLE}` : DEFAULT_TITLE;
  const description = projectOverview ?? DEFAULT_DESCRIPTION;
  const ogUrl = `${MAIN_DOMAIN}/${SITE_MAP.PROJECT.INDEX}/${detailProjectPathname}`;
  const ogImage = (mediaUrls?.[0] as FileWithFullUrls)?.originalUrl ?? DEFAULT_OG_IMAGE;

  return (
    <Fragment>
      <Head>
        <meta
          name='viewport'
          content='width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover'
        />
        <meta name='description' content={description} />
        <meta name='og:title' content={title} />
        <meta name='og:description' content={description} />
        <meta name='og:url' content={ogUrl} />
        <meta name='og:image' content={ogImage} />
        <title>{title}</title>
      </Head>
      <DetailProjectPageContext.Provider value={restProps}>
        {isEqual(device, 'desktop') ? (
          project ? (
            <DetailProject />
          ) : (
            <Error404 />
          )
        ) : isEqual(device, 'mobile') ? (
          project ? (
            <MobileDetailProject />
          ) : (
            <MobileError404 />
          )
        ) : null}
      </DetailProjectPageContext.Provider>
    </Fragment>
  );
};

export default DetailProjectPage;
